import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import LogoLoader from "../components/LogoLoader";
import { HelpCircle, Anchor, Ship, Truck, ShieldCheck } from "lucide-react";
import seoData from "../assets/data/seo.json";
import { Helmet } from "react-helmet-async";


// Flattened FAQ data (no categories)
const faqCategories = [
  {
    title: "General Questions",
    icon: HelpCircle,
    items: [
      {
        question: "Where is Shivam Shipping & Logistics - SCM located?",
        answer: "Our head office is strategically located in Paradip, Odisha. Our full address is Plot No. 1523/1975, Cuttack Paradip Road, Near Hotel Nandi Ghosh, Paradeepgarh, Paradip, Jagatsinghpur, Odisha, 754141, INDIA.",
      },
      {
        question: "How many years of experience does the company have?",
        answer: "We are a seasoned firm with over 30 years of experience in the shipping and logistics industry. Our operator is deeply committed to delivering exceptional solutions to streamline global trade.",
      },
      {
        question: "What makes Shivam Shipping & Logistics - SCM different from other agencies?",
        answer: "We offer a Cross-Functional Supply Chain Management approach. Instead of just handling one part of the process, we integrate Shipping Agency services, Customs clearance, Cargo handling, and Inland transportation to provide a seamless experience for our clients.",
      },
    ],
  },
  {
    title: "Services & Operations",
    icon: Ship,
    items: [
      {
        question: "Do you provide Customs House Agent (CHA) services?",
        answer: "Yes, we operate as a fully functional Customs House Agent (CHA), assisting clients with the complex documentation and clearance processes required for imports and exports.",
      },
      {
        question: "What types of cargo can you handle?",
        answer: "We are equipped to handle a wide variety of cargo types, including Bulk, Breakbulk, Containerized, and Project Cargo. We also specialize in the transportation of bulk materials. We also arrange transportation of related cargo",
      },
      {
        question: "Do you offer heavy equipment for site operations?",
        answer: "Yes. We provide Cargo Equipment Provision and can supply equipment in accordance with the cargo specification. We also supply essential Tools and Tackles for various operational needs.",
      },
      {
        question: "Can you handle vessel provisions?",
        answer: "Absolutely. We offer Ship Store and Provision Supply services to ensure that vessels docked at the port are restocked efficiently with the necessary supplies.",
      },
    ],
  },
  {
    title: "Logistics & Transportation",
    icon: Truck,
    items: [
      {
        question: "What modes of transport do you use for inland logistics?",
        answer: "We offer multi-modal transport solutions. We manage cargo movement via Road, Rake (Rail), and Inland Waterways, allowing us to choose the most efficient route for your goods.",
      },
      {
        question: "Do you act as Ship Agents for vessels calling at port?",
        answer: "Yes, our core services include acting as a Shipping Agency / Ship Agents. We represent the vessel owner/charterer and handle all port formalities and requirements.",
      },
    ],
  },
  {
    title: "Compliance & Safety",
    icon: ShieldCheck,
    items: [
      {
        question: "How can I request a quote for your services?",
        answer: "You can request a quote by emailing us at business@sslscm.com or by calling our support lines at +917016210802.",
      },
      {
        question: "Do you handle projects outside of Odisha?",
        answer: "While our base is in Paradip, our Supply Chain Management capabilities allow us to facilitate global trade solutions. Please contact us to discuss specific project locations and logistics requirements. We can manage PAN India.",
      },
    ],
  },
];

function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        className="flex justify-between items-center w-full py-4 text-left hover:bg-slate-50/50 rounded-xl px-4 transition-all duration-300"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <span className={`font-bold text-[18px] uppercase tracking-wider transition-colors leading-tight ${isOpen ? 'text-primary' : 'text-black'}`}>{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-[500px] opacity-100 pb-4" : "max-h-0 opacity-0"
          }`}
      >
        <div className="px-6 flex gap-4">
          <div className="w-1 h-full bg-primary/20 rounded-full"></div>
          <p className="text-slate-600 leading-relaxed font-medium italic text-[14px]">{answer}</p>
        </div>
      </div>
    </div>
  );
}


export default function FAQSection() {
  const [openId, setOpenId] = useState("General Questions-0");
  const [loading, setLoading] = useState(true);

  const { title, description, keywords, canonical, ogImage } = seoData.faq;

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) return <LogoLoader isLoading={loading} />;

  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <link rel="canonical" href={canonical} />
      </Helmet>
      {/* Hero Header */}
      <section className="bg-black py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[100px] -mr-32 -mt-32" />
        <div className="container mx-auto px-6 text-center relative z-10 max-w-4xl">
          <div className="inline-flex items-center space-x-2 text-secondary font-bold uppercase tracking-[0.2em] text-[12px] mb-4">
            <Anchor className="w-4 h-4" />
            <span>Support Center</span>
          </div>
          <h1 className="text-[34px] font-TuskerGrotesk text-white mb-4 uppercase italic tracking-widest leading-none">
            FREQUENTLY ASKED <span className="text-primary italic">QUESTIONS</span>
          </h1>
          <div className="w-16 h-1 bg-primary/40 mx-auto rounded-full mb-6" />
          <p className="text-slate-400 text-[16px] font-medium max-w-2xl mx-auto italic">
            Clarity in every operation. Everything you need to know about our specialized maritime and supply chain solutions.
          </p>
        </div>
      </section>

      <section className="px-6 max-w-5xl mx-auto py-12 -mt-10 relative z-20">
        <div className="space-y-8">
          {faqCategories.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.title} className="bg-white rounded-3xl shadow-2xl shadow-black/5 border border-slate-100 overflow-hidden transform transition-all hover:scale-[1.005]">
                <div className="p-6 md:p-8">
                  <div className="flex items-center space-x-4 mb-6 border-b border-slate-50 pb-4">
                    <div className="p-3 bg-primary/5 rounded-xl text-primary">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-[34px] font-TuskerGrotesk text-black uppercase tracking-wider italic leading-none">
                      {category.title}
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {category.items.map((item, index) => {
                      const itemId = `${category.title}-${index}`;
                      return (
                        <FAQItem
                          key={itemId}
                          question={item.question}
                          answer={item.answer}
                          isOpen={openId === itemId}
                          onClick={() => setOpenId(openId === itemId ? null : itemId)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Closing CTA Info */}
        <div className="mt-16 text-center bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-xl">
          <h3 className="text-[34px] font-TuskerGrotesk text-black mb-4 uppercase tracking-wider leading-none">Still have questions?</h3>
          <p className="text-slate-500 mb-8 max-w-xl mx-auto font-medium text-[14px]">Our logistics experts are available 24/7 to provide detailed guidance for your specific operational needs.</p>
          <a
            href="/contactus"
            className="inline-flex items-center gap-3 bg-black hover:bg-primary text-white font-bold py-4 px-10 rounded-xl transition-all duration-500 shadow-2xl uppercase tracking-widest text-[16px]"
          >
            Reach Out Directly
          </a>
        </div>
      </section>

      {/* Footer Decoration */}
      <footer className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4 text-center">
          <div className="w-px h-16 bg-gradient-to-b from-primary to-transparent mx-auto mb-8"></div>
          <Ship className="text-primary/10 w-12 h-12 mx-auto mb-6 animate-pulse" />
          <p className="text-slate-400 text-[10px] font-bold tracking-[0.4rem] uppercase italic leading-none">Operational Transparency Since 1996</p>
        </div>
      </footer>
    </div>
  );
}
